document.addEventListener('alpine:init', () => {
    Alpine.store('templateCreator', {
        // State
        templateTypes: [],
        selectedType: null,
        selectedDetails: {},
        searchTerm: '',
        activeTab: 'basic',
        previousTab: 'basic',
        viewMode: 'tile',
        showConfiguration: false,
        showAiGenerator: false,
        // Entry chooser: when true, the modal opens on the Guided-vs-Advanced
        // chooser instead of the type list. cameFromChooser tracks whether the
        // user reached the type list via the chooser (drives the Back control).
        showChooser: true,
        cameFromChooser: false,
        templateName: '',
        templateSlug: '',
        isCreating: false,
        isSuperAdmin: false,
        
        // Initialize with data from Livewire
        init() {
            // Template types will be injected from the Livewire component
            // Listen for the event from Livewire
            window.addEventListener('template-types-loaded', (event) => {
                console.log('Template types loaded:', event.detail.types?.length || 0, 'types');
                console.log('Is super admin:', event.detail.isSuperAdmin);
                this.templateTypes = event.detail.types || [];
                this.isSuperAdmin = event.detail.isSuperAdmin || false;
                // The component decides whether to show the entry chooser — it is
                // skipped for a preselected type and for the full-page
                // /flows/create integration route. Defaults to true (normal open).
                this.showChooser = event.detail.showChooser ?? true;
            });
            
            // Listen for the go-back event from Livewire
            window.addEventListener('trigger-alpine-go-back', () => {
                console.log('Go back event received');
                this.goBack();
            });

            // Listen for a pre-selection request (e.g. modal opened with a specific type)
            window.addEventListener('preselect-template-type', (event) => {
                const type = event.detail.type;
                const details = this.templateTypes.find((t) => t.type === type);
                if (details) {
                    this.selectType(type, details);
                }
            });
            
            // Listen for the reset event when modal closes
            window.addEventListener('reset-template-creator-store', () => {
                console.log('Reset template creator store event received');
                this.reset();
            });
            
            // Listen for Livewire events directly
            Livewire.on('reset-template-creator-store', () => {
                console.log('Reset template creator store event received (via Livewire.on)');
                this.reset();
            });
        },
        
        // Computed getters
        get filteredTemplates() {
            let filtered = [...this.templateTypes];
            
            // Filter by search term
            if (this.searchTerm) {
                const searchLower = this.searchTerm.toLowerCase();
                filtered = filtered.filter(template => {
                    const label = (template.label || '').toLowerCase();
                    const description = (template.description || '').toLowerCase();
                    return label.includes(searchLower) || description.includes(searchLower);
                });
            }
            
            // Filter by active tab/category
            filtered = filtered.filter(template => {
                const category = template.category || 'basic';
                return category === this.activeTab;
            });
            
            return filtered;
        },
        
        get isFormValid() {
            return this.templateName.length >= 4 && 
                   this.templateSlug.length > 0 && 
                   this.selectedType !== null;
        },
        
        get showAddressConfig() {
            // Check if template explicitly doesn't require a page
            if (this.selectedDetails.requires_page === false) {
                return false;
            }
            return this.selectedType !== 'webhook' && this.selectedType !== 'dynamic_schema';
        },
        
        get isMailbox() {
            return this.selectedType === 'mailbox';
        },
        
        get isWebhook() {
            return this.selectedType === 'webhook';
        },
        
        get isDynamicSchema() {
            return this.selectedType === 'dynamic_schema';
        },
        
        get hasFields() {
            return this.selectedDetails.fields && Object.keys(this.selectedDetails.fields).length > 0;
        },

        get hideFields() {
            return this.selectedDetails.hide_fields === true;
        },

        get fields() {
            return this.selectedDetails.fields || {};
        },
        
        // Actions
        selectType(type, details) {
            // Don't select if coming soon (unless user is super admin)
            if (details.coming_soon && !this.isSuperAdmin) {
                return;
            }
            
            this.selectedType = type;
            this.selectedDetails = details;
            this.showConfiguration = true;
            this.showAiGenerator = false;
            this.previousTab = this.activeTab; // Store the current tab before navigating
            
            // Reset form fields when selecting a new type
            this.templateName = '';
            this.templateSlug = '';
        },
        
        setActiveTab(tab) {
            // Special handling for AI generator
            if (tab === 'generator') {
                this.showAiGenerator = true;
                this.showConfiguration = false;
                return;
            }
            
            this.activeTab = tab;
            this.showAiGenerator = false;
        },
        
        setViewMode(mode) {
            this.viewMode = mode;
        },
        
        goBack() {
            console.log('goBack');
            this.showConfiguration = false;
            this.showAiGenerator = false;
            this.selectedType = null;
            this.selectedDetails = {};
            this.templateName = '';
            this.templateSlug = '';
            // Restore the previous tab instead of always going to 'basic'
            this.activeTab = this.previousTab || 'basic';
        },

        chooseAdvanced() {
            // Reveal the full template-type list inside the same modal.
            this.showChooser = false;
            this.cameFromChooser = true;
        },

        backToChooser() {
            // Return to the Guided-vs-Advanced chooser from the type list.
            this.showChooser = true;
        },

        updateTemplateName(name) {
            this.templateName = name;
            // Auto-generate slug from name
            this.templateSlug = this.generateSlug(name);
        },
        
        updateTemplateSlug(slug) {
            this.templateSlug = slug;
        },
        
        generateSlug(text) {
            return text.toLowerCase()
                .replace(/[^a-z0-9 -]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .trim()
                .substring(0, 255); // Limit length
        },
        
        startCreating() {
            this.isCreating = true;
        },
        
        stopCreating() {
            this.isCreating = false;
        },
        
        reset() {
            console.log('Resetting templateCreator store to initial state');
            this.selectedType = null;
            this.selectedDetails = {};
            this.searchTerm = '';
            this.activeTab = 'basic';
            this.previousTab = 'basic';
            this.viewMode = 'tile';
            this.showConfiguration = false;
            this.showAiGenerator = false;
            this.showChooser = true;
            this.cameFromChooser = false;
            this.templateName = '';
            this.templateSlug = '';
            this.isCreating = false;
            // Don't reset isSuperAdmin as it's based on user role which doesn't change
            console.log('Reset complete - activeTab:', this.activeTab, 'showConfiguration:', this.showConfiguration, 'showAiGenerator:', this.showAiGenerator);
        }
    });
});